import React, { useState, useEffect, useMemo } from 'react';
import { formatCurrency } from '../utils/currencyUtils';
import { useParams } from 'react-router-dom';
import { Building } from '../types/building';
import { LL97Service } from '../services/ll97Service';
import { RetrofitService } from '../services/retrofitService';

interface SimpleBuildingViewProps {
    buildings: Building[];
    onBack: () => void;
}

interface SimpleRetrofit {
    name: string;
    description: string;
    cost: number;
    savings: string;
    payback: string;
    priority: 'High' | 'Medium' | 'Low';
}

const SimpleBuildingView: React.FC<SimpleBuildingViewProps> = ({ buildings, onBack }) => {
    const { buildingId } = useParams<{ buildingId: string }>();
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedBuilding, setSelectedBuilding] = useState<Building | null>(null);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [ll97Status, setLL97Status] = useState<any>(null);

    // Simple retrofit recommendations
    const simpleRetrofits: SimpleRetrofit[] = [
        {
            name: "LED Lighting Upgrade",
            description: "Replace all lighting with energy-efficient LED bulbs and fixtures",
            cost: 25000,
            savings: "$3,200/year",
            payback: "7.8 years",
            priority: "High"
        },
        {
            name: "Smart Thermostat Installation",
            description: "Install programmable smart thermostats for better temperature control",
            cost: 8000,
            savings: "$1,800/year",
            payback: "4.4 years",
            priority: "High"
        },
        {
            name: "Window Insulation",
            description: "Add weather stripping and improve window sealing",
            cost: 15000,
            savings: "$2,100/year",
            payback: "7.1 years",
            priority: "Medium"
        },
        {
            name: "HVAC Tune-up",
            description: "Professional maintenance and optimization of heating/cooling systems",
            cost: 5000,
            savings: "$1,200/year",
            payback: "4.2 years",
            priority: "High"
        }
    ];

    // Filter buildings for search
    const filteredBuildings = useMemo(() => {
        if (!searchQuery.trim()) return [];
        return buildings.filter(building =>
            building.address.toLowerCase().includes(searchQuery.toLowerCase()) ||
            building.propertyName.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [searchQuery, buildings]);

    // Load building from URL or selection
    useEffect(() => {
        if (buildingId && buildings.length > 0) {
            const building = buildings.find(b => b.id === buildingId);
            if (building) {
                setSelectedBuilding(building);
                setSearchQuery(building.address || building.propertyName);
                setShowSuggestions(false);
            }
        }
    }, [buildingId, buildings]);

    // Calculate LL97 status when building is selected
    useEffect(() => {
        if (selectedBuilding) {
            const ll97LimitPerSqFt = LL97Service.getEmissionLimit(selectedBuilding.buildingType, 2025);
            const currentEmissions = selectedBuilding.totalGHGEmissions;
            const allowedEmissions = ll97LimitPerSqFt * selectedBuilding.grossFloorArea;
            
            // Gap = Current Emissions - Allowed Emissions (only if current exceeds allowed)
            const gapAmount = Math.max(0, currentEmissions - allowedEmissions);
            const gapPerSqFt = gapAmount / selectedBuilding.grossFloorArea;
            
            const finePerTon = 268; // LL97 fine rate ($268 per metric ton CO2e)
            const annualFine = gapAmount > 0 ? gapAmount * finePerTon : 0;
            
            setLL97Status({
                currentEmissions,
                allowedEmissions,
                gapAmount,
                gapPerSqFt,
                annualFine,
                limitPerSqFt: ll97LimitPerSqFt,
                totalGap: gapAmount
            });
        }
    }, [selectedBuilding]);

    const handleBuildingSelect = (building: Building) => {
        setSelectedBuilding(building);
        setSearchQuery(building.address);
        setShowSuggestions(false);
    };

    const getPriorityColor = (priority: 'High' | 'Medium' | 'Low') => {
        switch (priority) {
            case 'High': return 'bg-danger-100 text-danger-800';
            case 'Medium': return 'bg-warning-100 text-warning-800';
            case 'Low': return 'bg-success-100 text-success-800';
        }
    };

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(amount);
    };

    return (
        <div className="min-h-screen bg-neutral-50 py-8">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-8">
                    <button
                        onClick={onBack}
                        className="mb-4 text-primary-600 hover:text-primary-800 flex items-center text-lg"
                    >
                        ← Back to Dashboard
                    </button>
                    <h1 className="text-4xl font-bold text-neutral-900 mb-2">Building Manager View</h1>
                    <p className="text-xl text-neutral-600">Simple energy analysis for your building</p>
                    
                    {/* Usage Instructions */}
                    <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h3 className="text-lg font-semibold text-blue-900 mb-3 flex items-center">
                            <span className="mr-2">📋</span> How to Use This Tool
                        </h3>
                        <ol className="list-decimal list-inside space-y-1 text-sm text-blue-800">
                            <li>Enter your building address or property name in the search box</li>
                            <li>Select your building from the suggestions</li>
                            <li>Review the simplified compliance dashboard</li>
                            <li>Check your LL97 status and potential fines</li>
                            <li>Explore priority retrofit recommendations</li>
                        </ol>
                        <p className="text-xs text-blue-700 mt-3">
                            💡 <strong>Tip:</strong> This simplified view is perfect for property managers who need quick insights without detailed technical analysis.
                        </p>
                    </div>
                </div>

                {/* Building Search */}
                <div className="bg-white rounded-xl shadow-md p-8 mb-8">
                    <h2 className="text-2xl font-semibold text-neutral-900 mb-6">Find Your Building</h2>
                    
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Type your building address..."
                            value={searchQuery}
                            onChange={(e) => {
                                setSearchQuery(e.target.value);
                                setShowSuggestions(true);
                            }}
                            onFocus={() => setShowSuggestions(true)}
                            className="w-full p-4 text-lg border-2 border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                        
                        {showSuggestions && filteredBuildings.length > 0 && (
                            <div className="absolute z-10 w-full bg-white border-2 border-neutral-300 rounded-lg mt-2 max-h-60 overflow-y-auto shadow-lg">
                                {filteredBuildings.slice(0, 10).map((building) => (
                                    <div
                                        key={building.id}
                                        className="p-4 hover:bg-primary-50 cursor-pointer border-b last:border-b-0"
                                        onClick={() => handleBuildingSelect(building)}
                                    >
                                        <div className="font-medium text-neutral-900">{building.propertyName || building.address}</div>
                                        <div className="text-sm text-neutral-600">{building.address} • {building.borough}</div>
                                        <div className="text-sm text-primary-600">{building.buildingType} • {building.grossFloorArea.toLocaleString()} sq ft</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {selectedBuilding && (
                    <>
                        {/* Building Overview */}
                        <div className="bg-white rounded-xl shadow-md p-8 mb-8">
                            <h2 className="text-2xl font-semibold text-neutral-900 mb-6">Your Building</h2>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-4">
                                    <div>
                                        <h3 className="text-lg font-medium text-neutral-700">Property</h3>
                                        <p className="text-xl font-semibold">{selectedBuilding.propertyName || selectedBuilding.address}</p>
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-medium text-neutral-700">Location</h3>
                                        <p className="text-xl">{selectedBuilding.address}</p>
                                        <p className="text-lg text-neutral-600">{selectedBuilding.borough}, NY {selectedBuilding.zipCode}</p>
                                    </div>
                                </div>
                                <div className="space-y-4">
                                    <div>
                                        <h3 className="text-lg font-medium text-neutral-700">Building Type</h3>
                                        <p className="text-xl">{selectedBuilding.buildingType}</p>
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-medium text-neutral-700">Size</h3>
                                        <p className="text-xl">{selectedBuilding.grossFloorArea.toLocaleString()} square feet</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* LL97 Status - Simplified */}
                        {ll97Status && (
                            <div className={`rounded-xl shadow-md p-8 mb-8 ${ll97Status.gapPerSqFt > 0 ? 'bg-danger-50 border-2 border-danger-200' : 'bg-success-50 border-2 border-success-200'}`}>
                                <div className="flex items-center mb-4">
                                    <div className={`w-4 h-4 rounded-full mr-3 ${ll97Status.gapPerSqFt > 0 ? 'bg-danger-500' : 'bg-success-500'}`}></div>
                                    <h2 className="text-2xl font-semibold">
                                        {ll97Status.gapPerSqFt > 0 ? 'Action Required' : 'You\'re In Compliance!'}
                                    </h2>
                                </div>
                                
                                {ll97Status.gapPerSqFt > 0 ? (
                                    <div className="space-y-4">
                                        <p className="text-lg">Your building needs to reduce emissions to avoid fines starting in 2025.</p>
                                        <div className="bg-white rounded-lg p-6">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-center">
                                                <div>
                                                    <h3 className="text-lg font-medium text-neutral-700 mb-2">Potential Annual Fine</h3>
                                                    <p className="text-3xl font-bold text-danger-600">{formatCurrency(ll97Status.annualFine)}</p>
                                                </div>
                                                <div>
                                                    <h3 className="text-lg font-medium text-neutral-700 mb-2">Emissions to Reduce</h3>
                                                    <p className="text-3xl font-bold text-warning-600">{ll97Status.totalGap.toFixed(0)} tons CO2</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <p className="text-lg">Great! Your building meets the Local Law 97 requirements. Consider the improvements below to save even more money.</p>
                                )}
                            </div>
                        )}

                        {/* Simple Energy Tips */}
                        <div className="bg-white rounded-xl shadow-md p-8 mb-8">
                            <h2 className="text-2xl font-semibold text-neutral-900 mb-6">Recommended Improvements</h2>
                            <p className="text-lg text-neutral-600 mb-6">These simple upgrades can save you money and reduce emissions:</p>
                            
                            <div className="space-y-6">
                                {simpleRetrofits.map((retrofit, index) => (
                                    <div key={index} className="border-2 border-neutral-200 rounded-lg p-6 hover:border-primary-300 transition-colors">
                                        <div className="flex items-start justify-between mb-4">
                                            <div className="flex-1">
                                                <h3 className="text-xl font-semibold text-neutral-900 mb-2">{retrofit.name}</h3>
                                                <p className="text-neutral-600 text-lg">{retrofit.description}</p>
                                            </div>
                                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getPriorityColor(retrofit.priority)}`}>
                                                {retrofit.priority} Priority
                                            </span>
                                        </div>
                                        
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                                            <div className="text-center p-4 bg-primary-50 rounded-lg">
                                                <h4 className="text-sm font-medium text-neutral-600">Initial Cost</h4>
                                                <p className="text-xl font-bold text-primary-600">{formatCurrency(retrofit.cost)}</p>
                                            </div>
                                            <div className="text-center p-4 bg-success-50 rounded-lg">
                                                <h4 className="text-sm font-medium text-neutral-600">Annual Savings</h4>
                                                <p className="text-xl font-bold text-success-600">{retrofit.savings}</p>
                                            </div>
                                            <div className="text-center p-4 bg-accent-50 rounded-lg">
                                                <h4 className="text-sm font-medium text-neutral-600">Payback Period</h4>
                                                <p className="text-xl font-bold text-accent-600">{retrofit.payback}</p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Next Steps */}
                        <div className="bg-primary-50 rounded-xl shadow-md p-8 mb-8">
                            <h2 className="text-2xl font-semibold text-neutral-900 mb-6">What Should I Do Next?</h2>
                            
                            <div className="space-y-4">
                                <div className="flex items-start">
                                    <div className="bg-primary-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4 mt-1">1</div>
                                    <div>
                                        <h3 className="text-lg font-semibold mb-2">Start with High Priority Items</h3>
                                        <p className="text-neutral-700">Focus on LED lighting and HVAC tune-ups first - they have the fastest payback.</p>
                                    </div>
                                </div>
                                
                                <div className="flex items-start">
                                    <div className="bg-primary-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4 mt-1">2</div>
                                    <div>
                                        <h3 className="text-lg font-semibold mb-2">Get Professional Quotes</h3>
                                        <p className="text-neutral-700">Contact certified contractors for detailed estimates and timeline.</p>
                                    </div>
                                </div>
                                
                                <div className="flex items-start">
                                    <div className="bg-primary-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4 mt-1">3</div>
                                    <div>
                                        <h3 className="text-lg font-semibold mb-2">Look for Incentives</h3>
                                        <p className="text-neutral-700">Check for rebates from NYSERDA, Con Edison, and other programs to reduce costs.</p>
                                    </div>
                                </div>
                                
                                <div className="flex items-start">
                                    <div className="bg-primary-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4 mt-1">4</div>
                                    <div>
                                        <h3 className="text-lg font-semibold mb-2">Consider Financing</h3>
                                        <p className="text-neutral-700">CPACE financing lets you pay for improvements over time with property taxes.</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Simple Export */}
                        <div className="bg-white rounded-xl shadow-md p-8">
                            <h2 className="text-2xl font-semibold text-neutral-900 mb-6">Share This Report</h2>
                            <p className="text-lg text-neutral-600 mb-6">Get a simple summary to share with your team or contractors:</p>
                            
                            <button
                                onClick={() => window.print()}
                                className="bg-primary-600 text-white px-8 py-4 rounded-lg hover:bg-primary-700 transition-colors text-lg font-medium"
                            >
                                Print Building Summary
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default SimpleBuildingView;
